import { Component } from '@angular/core';
import { IDoesFilterPassParams, IFilterParams, RowNode } from 'ag-grid-community';
import { IFilterAngularComp } from 'ag-grid-angular';
import { Subject, BehaviorSubject, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, switchMap } from 'rxjs/operators';
import { isNumber } from '@datorama/akita';

@Component({
  selector: 'app-custom-filter',
  templateUrl: './custom-filter.component.html',
  styleUrls: ['./custom-filter.component.scss'],
})
export class CustomFilterComponent implements IFilterAngularComp {
  private static readonly rowHeight: number = 28;

  private model: Array<string>;
  private uniqueCheck: object;
  private filterParams: IFilterParams;
  private fieldId: string;
  private filterList: Array<string>;
  private filteredList: Array<string>;
  private ngUnsubscribe: Subject<any>;

  /**
   * Indicator to check if all items are selected
   */
  isEverythingSelected = false;
  /**
   * The height of the container holding our list of unique values
   * for the given column. The height is determined by the number of
   * items * rowHeight (28px);
   */
  containerHeight: number;
  /**
   * Using an object to store our selected values. The lookup will be
   * faster on a simple object, then using an array to filter or using a Map.
   */
  selectedValuesMap: object;
  /**
   * Holds our list of values for the user to select from.
   */
  onFilterListChange$: BehaviorSubject<string[]>;
  onFilterValuesChanged$: Observable<string[]>;

  items$: Observable<string[]>;
  filteredItems$: Observable<string[]>;
  searchTerm$: Subject<string>;

  isBooleanFilter = false;

  agInit(params: IFilterParams): void {
    this.filterParams = params;
    this.uniqueCheck = {};
    this.selectedValuesMap = {};
    this.filterList = new Array<string>();

    this.fieldId = params.colDef.field;

    this.onFilterListChange$ = new BehaviorSubject<string[]>([]);
    this.ngUnsubscribe = new Subject<any>();

    this.searchTerm$ = new Subject<string>();

    // this.filteredItems$ = this.onFilterListChange$.pipe(
    //   debounceTime(250),
    //   tap((values) => console.log(values)),
    //   tap((values) => this.sortValues(values)),
    //   tap(() => this.setContainerHeight())
    // );

    this.setFilteredItems();

    this.filteredItems$.subscribe((d) => {
      this.filteredList = d;
    });

    // this.onFilterValuesChanged$ = this.onFilterListChange$.pipe(
    //   debounceTime(250),
    //   tap((values) => this.sortValues(values)),
    //   tap((values) => this.setContainerHeight()),
    // );

    this.setUniqueValues();
    this.sortValues(this.filterList);
    this.setContainerHeight();
    this.filteredList = this.filterList;
    // this.selectEverything();
  }

  setUniqueValues() {
    let isBoolean = true;

    this.filterParams.rowModel.forEachNode((node: RowNode) => {
      if (!node.data) {
        return;
      }

      const value = node.data[this.fieldId];

      if (typeof value !== 'boolean') {
        isBoolean = false;
        this.addUniqueValueIfMissing(value ? value.toString() : '');
      } else {
        this.addUniqueValueIfMissing(value.toString());
      }
    });

    this.isBooleanFilter = isBoolean;
  }

  toggleItem(value: string) {
    if (this.selectedValuesMap[value]) {
      delete this.selectedValuesMap[value];
    } else {
      this.selectedValuesMap[value] = 1;
    }

    this.isEverythingSelected =
      Object.keys(this.selectedValuesMap).length === this.filterList.length;

    this.onFilterChanged();
  }

  toggleEverything() {
    this.isEverythingSelected = !this.isEverythingSelected;
    if (this.isEverythingSelected) {
      this.selectEverything();
    } else {
      this.unselectEverything();
    }
    this.onFilterChanged();
  }

  onNewRowsLoaded() {
    setTimeout(() => {
      this.clearFilterData();
      this.setFilteredItems();
      this.setUniqueValues();
      this.sortValues(this.filterList);
    }, 100);
  }

  isFilterActive(): boolean {
    return (
      Object.keys(this.selectedValuesMap).length > 0 &&
      !this.isEverythingSelected
    );
  }

  doesFilterPass(params: IDoesFilterPassParams): boolean {
    const node = params.node;
    if (!node.data) {
      return false;
    }

    const nodeValue = params.data[this.fieldId] === null ? '' : params.data[this.fieldId];
    if (!this.isValueValid(nodeValue)) {
      return false;
    }

    if (typeof nodeValue === 'string') {
      return Object.keys(this.selectedValuesMap).some(selectedValue => nodeValue.includes(selectedValue));
    }
    else {
      return Boolean(this.selectedValuesMap[nodeValue]);
    }

    // Ha a kiválasztott érték szerepel a nodeValue-ban, akkor engedélyezzük a sort
  }

  getModel() {
    return this.selectedValuesMap;
  }

  onFilterChanged() {
    this.filterParams.filterChangedCallback();
  }

  setModel(values: string[]) {
    this.unselectEverything();
    if (values) {
      values.forEach((value) => {
        this.selectedValuesMap[value] = 1;
        this.addUniqueValueIfMissing(value);
      });
    }
  }

  destroy() {
    this.ngUnsubscribe.complete();
  }

  selectAll() {
    this.filteredList.forEach((value) => (this.selectedValuesMap[value] = 1));
    // this.isEverythingSelected = true;

    this.onFilterChanged();
  }

  deselectAll() {
    this.selectedValuesMap = {};

    this.onFilterChanged();
  }

  resetSearch() {
    this.searchTerm$.next('');
  }

  private setContainerHeight() {
    this.containerHeight =
      this.filterList.length * CustomFilterComponent.rowHeight;
  }

  private sortValues(values: string[]) {
    let isNumberArray = false;
    if (values.every((item) => isNumber(item) || item === '')) {
      isNumberArray = true;
    }

    if (isNumberArray) {
      values.sort((s1, s2) => {
        const n1 = isNumber(s1) ? Number(s1) : -Number.MAX_SAFE_INTEGER;
        const n2 = isNumber(s2) ? Number(s2) : -Number.MAX_SAFE_INTEGER;

        if (n1 > n2) {
          return 1;
        }

        if (n1 < n2) {
          return -1;
        }

        return 0;
      });
    } else {
      values.sort();
    }
  }

  private selectEverything() {
    this.filterList.forEach((value) => (this.selectedValuesMap[value] = 1));
    this.isEverythingSelected = true;
  }

  private unselectEverything() {
    this.selectedValuesMap = {};
    this.isEverythingSelected = false;
  }

  private addUniqueValueIfMissing(value: string) {
    if (!this.isValueValid(value)) {
      return;
    }

    const values = value.split(',').map(item => item.trim()).filter(item => item != '');

    values.forEach(val => {
      if (this.uniqueCheck[val]) {
        return;
      }

      this.filterList.push(val);
      this.uniqueCheck[val] = 1;
    });

    this.onFilterListChange$.next([...this.filterList]);
  }


  private isValueValid(value: string) {
    // value === '' ||
    if (value === undefined || value === null) {
      return false;
    }

    return true;
  }

  private setFilteredItems() {
    this.filteredItems$ = this.searchTerm$.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      startWith(''),
      switchMap((val) => {
        return of(
          this.filterList.filter(
            (item) => item.toLowerCase().indexOf(val.toLowerCase()) !== -1
          )
        );
      })
    );
  }

  public onFilterTextChanged($event): void {
    this.searchTerm$.next(($event.target as HTMLInputElement).value.toString())
  }

  private clearFilterData() {
    this.filterList.forEach((val) => {
      this.uniqueCheck[val] = 0;
    });

    this.selectedValuesMap = {};
    this.filterList = [];
    this.filteredList = [];
    this.setFilteredItems();
  }
}
// export class CustomFilterComponent implements IFilterAngularComp {
//   private static readonly rowHeight: number = 28;

//   private model: Array<string>;
//   private uniqueCheck: object;
//   private filterParams: IFilterParams;
//   private fieldId: string;
//   private filterList: Array<string>;
//   private ngUnsubscribe: Subject<any>;

//   /**
//    * Indicator to check if all items are selected
//    */
//   isEverythingSelected: boolean;
//   /**
//    * The height of the container holding our list of unique values
//    * for the given column. The height is determined by the number of
//    * items * rowHeight (28px);
//    */
//   containerHeight: number;
//   /**
//    * Using an object to store our selected values. The lookup will be
//    * faster on a simple object, then using an array to filter or using a Map.
//    */
//   selectedValuesMap: object;
//   /**
//    * Holds our list of values for the user to select from.
//    */
//   onFilterListChange$: BehaviorSubject<string[]>;
//   onFilterValuesChanged$: Observable<string[]>;

//   agInit(params: IFilterParams): void {
//     this.filterParams = params;
//     this.uniqueCheck = {};
//     this.selectedValuesMap = {};
//     this.filterList = new Array<string>();

//     this.fieldId = params.colDef.field;

//     this.onFilterListChange$ = new BehaviorSubject<string[]>([]);
//     this.ngUnsubscribe = new Subject<any>();

//     this.onFilterValuesChanged$ = this.onFilterListChange$.pipe(
//       debounceTime(250),
//       tap((values) => this.sortValues(values)),
//       tap((values) => this.setContainerHeight())
//     );

//     this.setUniqueValues();
//     this.selectEverything();
//   }

//   setUniqueValues() {
//     this.filterParams.rowModel.forEachNode((node: RowNode) => {
//       if (!node.data) {
//         return;
//       }

//       const value = node.data[this.fieldId];
//       this.addUniqueValueIfMissing(value);
//     });
//   }

//   toggleItem(value: string) {
//     if (this.selectedValuesMap[value]) {
//       delete this.selectedValuesMap[value];
//     } else {
//       this.selectedValuesMap[value] = 1;
//     }

//     this.isEverythingSelected =
//       Object.keys(this.selectedValuesMap).length === this.filterList.length;

//     this.onFilterChanged();
//   }

//   toggleEverything() {
//     this.isEverythingSelected = !this.isEverythingSelected;
//     if (this.isEverythingSelected) {
//       this.selectEverything();
//     } else {
//       this.unselectEverything();
//     }
//     this.onFilterChanged();
//   }

//   onNewRowsLoaded() {
//     this.setUniqueValues();
//   }

//   isFilterActive(): boolean {
//     return (
//       Object.keys(this.selectedValuesMap).length > 0 &&
//       !this.isEverythingSelected
//     );
//   }

//   doesFilterPass(params: IDoesFilterPassParams): boolean {
//     const node = params.node;
//     if (!node.data) {
//       return false;
//     }

//     const nodeValue = params.data[this.fieldId];
//     if (!this.isValueValid(nodeValue)) {
//       return false;
//     }

//     // check if current row node value exists in our selectedValuesMap
//     return Boolean(this.selectedValuesMap[nodeValue]);
//   }

//   getModel() {
//     return this.model;
//   }

//   onFilterChanged() {
//     this.filterParams.filterChangedCallback();
//   }

//   setModel(value: string) {
//     this.unselectEverything();
//     this.selectedValuesMap[value] = 1;
//     this.addUniqueValueIfMissing(value);
//   }

//   destroy() {
//     this.ngUnsubscribe.next();
//     this.ngUnsubscribe.complete();
//   }

//   private setContainerHeight() {
//     this.containerHeight =
//       this.filterList.length * CustomFilterComponent.rowHeight;
//   }

//   private sortValues(values: string[]) {
//     values.sort();
//   }

//   private selectEverything() {
//     this.filterList.forEach((value) => (this.selectedValuesMap[value] = 1));
//     this.isEverythingSelected = true;
//   }

//   private unselectEverything() {
//     this.selectedValuesMap = {};
//     this.isEverythingSelected = false;
//   }

//   private addUniqueValueIfMissing(value: string) {
//     if (!this.isValueValid(value)) {
//       return;
//     }

//     if (this.uniqueCheck[value]) {
//       return;
//     }

//     this.filterList.push(value);
//     this.onFilterListChange$.next([...this.filterList]);
//     this.uniqueCheck[value] = 1;
//   }

//   private isValueValid(value: string) {
//     if (value === '' || value === undefined || value === null) {
//       return false;
//     }

//     return true;
//   }
// }
